<script lang="ts">
  import type { FrameDetection } from '@/lib/onnxInference';
  import { brandStore } from '@/lib/brandStore.svelte';

  interface Props {
    detection: FrameDetection;
    onclick?: (d: FrameDetection) => void;
    selected?: boolean;
  }

  let { detection, onclick, selected = false }: Props = $props();

  const borderColor = $derived(
    detection.confidence >= 0.6 ? 'border-green-500' :
    detection.confidence >= 0.4 ? 'border-yellow-500' :
    'border-orange-400'
  );

  const confClass = $derived(
    detection.confidence >= 0.6 ? 'text-green-400' :
    detection.confidence >= 0.4 ? 'text-yellow-400' :
    'text-orange-400'
  );

  const categoryIcon = $derived(
    detection.className
      ? (brandStore.categories.find((c) => c.id === detection.className)?.icon ?? '')
      : ''
  );

  const categoryLabel = $derived(
    detection.className ? brandStore.translateCategory(detection.className) : ''
  );

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
</script>

<div
  class="rounded-xl overflow-hidden border-2 {borderColor} transition-all duration-200 cursor-pointer {selected ? 'ring-2 ring-blue-500' : ''} hover:scale-[1.02] hover:shadow-lg"
  role="button"
  tabindex="0"
  onclick={() => onclick?.(detection)}
  onkeydown={(e) => e.key === 'Enter' && onclick?.(detection)}
>
  <div class="relative w-full h-24">
    <img
      src={detection.thumbnailDataUrl}
      alt=""
      class="w-full h-full object-cover"
    />
    <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
    <span class="absolute top-2 right-2 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
      {formatTime(detection.time)}
    </span>
  </div>

  <div class="flex items-center gap-2 px-3 py-2 bg-white dark:bg-zinc-900">
    {#if categoryIcon}
      <span class="text-base leading-none">{categoryIcon}</span>
    {/if}
    {#if categoryLabel}
      <span class="text-sm font-medium text-zinc-700 dark:text-zinc-300 truncate flex-1 min-w-0">
        {categoryLabel}
      </span>
    {/if}
    <span class="text-xs font-semibold {confClass} shrink-0">
      {(detection.confidence * 100).toFixed(0)}%
    </span>
  </div>
</div>
